import { useState, useEffect, useCallback } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import axios from 'axios';
import { Plus, Pencil, Trash2, Upload, Eye, EyeOff, FileText, X, Download, TrendingUp, Wallet, Clock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const VISIBILITY_OPTIONS = [
  { value: 'yonetim', label: 'Sadece Yönetim', color: 'bg-red-100 text-red-700' },
  { value: 'ozet', label: 'Özet Paylaş', color: 'bg-amber-100 text-amber-700' },
  { value: 'tam_detay', label: 'Tam Detay Paylaş', color: 'bg-green-100 text-green-700' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'bekliyor', label: 'Bekliyor', color: 'bg-amber-100 text-amber-700' },
  { value: 'odendi', label: 'Ödendi', color: 'bg-green-100 text-green-700' },
  { value: 'kismi', label: 'Kısmi Ödeme', color: 'bg-blue-100 text-blue-700' },
];

const emptyForm = {
  name: '', category: '', amount: '', date: '', description: '',
  payment_status: 'bekliyor', payment_account: '', visibility: 'yonetim',
};

export const AdminExpenses = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterVisibility, setFilterVisibility] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterCategory !== 'all') params.append('category', filterCategory);
      if (filterVisibility !== 'all') params.append('visibility', filterVisibility);

      const [expRes, sumRes, catRes] = await Promise.all([
        axios.get(`${API_URL}/api/expenses?${params}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/expenses/summary`, { withCredentials: true }),
        axios.get(`${API_URL}/api/expenses/categories`, { withCredentials: true }),
      ]);
      setExpenses(expRes.data);
      setSummary(sumRes.data);
      setCategories(catRes.data);
    } catch (e) {
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [API_URL, filterCategory, filterVisibility]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, amount: parseFloat(formData.amount) };
      if (editingId) {
        await axios.put(`${API_URL}/api/expenses/${editingId}`, payload, { withCredentials: true });
        toast.success('Gider güncellendi');
      } else {
        await axios.post(`${API_URL}/api/expenses`, payload, { withCredentials: true });
        toast.success('Gider eklendi');
      }
      setDialogOpen(false);
      setEditingId(null);
      setFormData(emptyForm);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bir hata oluştu');
    }
  };

  const handleEdit = (exp) => {
    setEditingId(exp.id);
    setFormData({
      name: exp.name, category: exp.category, amount: String(exp.amount),
      date: exp.date, description: exp.description || '',
      payment_status: exp.payment_status, payment_account: exp.payment_account || '',
      visibility: exp.visibility,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu gideri silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/expenses/${id}`, { withCredentials: true });
      toast.success('Gider silindi');
      fetchData();
    } catch (err) {
      toast.error('Silinemedi');
    }
  };

  const handleFileUpload = async (expenseId, file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API_URL}/api/expenses/${expenseId}/upload`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Belge yüklendi');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Belge yüklenemedi');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (expenseId, filename) => {
    try {
      await axios.delete(`${API_URL}/api/expenses/${expenseId}/documents/${filename}`, { withCredentials: true });
      toast.success('Belge silindi');
      fetchData();
    } catch (err) {
      toast.error('Belge silinemedi');
    }
  };

  const openNewDialog = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setDialogOpen(true);
  };

  const getVisibilityBadge = (v) => {
    const opt = VISIBILITY_OPTIONS.find(o => o.value === v);
    return opt ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span> : v;
  };

  const getStatusBadge = (s) => {
    const opt = PAYMENT_STATUS_OPTIONS.find(o => o.value === s);
    return opt ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${opt.color}`}>{opt.label}</span> : s;
  };

  const fmt = (n) => new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

  if (loading) {
    return <PageLayout><div className="flex items-center justify-center min-h-[50vh]"><p className="text-[#595959]">Yükleniyor...</p></div></PageLayout>;
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#0F172A]" data-testid="expenses-title">
              Gider Yönetimi
            </h1>
            <p className="text-sm text-[#595959] mt-1">Apartman/site giderlerini yönetin ve paylaşın</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingId(null); setFormData(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#2563EB] hover:bg-[#1E4ED8] gap-2" onClick={openNewDialog} data-testid="add-expense-btn">
                <Plus className="w-4 h-4" /> Gider Ekle
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Gider Düzenle' : 'Yeni Gider Ekle'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="expense-form">
                <div className="space-y-1.5">
                  <Label>Masraf Adı *</Label>
                  <Input value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} required placeholder="Ör: Asansör bakımı" data-testid="expense-name-input" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Kategori *</Label>
                    <Select value={formData.category} onValueChange={(v) => setFormData(p => ({ ...p, category: v }))}>
                      <SelectTrigger data-testid="expense-category-select"><SelectValue placeholder="Seçin" /></SelectTrigger>
                      <SelectContent>
                        {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Tutar (TL) *</Label>
                    <Input type="number" step="0.01" min="0" value={formData.amount} onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))} required placeholder="0.00" data-testid="expense-amount-input" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Tarih *</Label>
                    <Input type="date" value={formData.date} onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))} required data-testid="expense-date-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Ödeme Durumu</Label>
                    <Select value={formData.payment_status} onValueChange={(v) => setFormData(p => ({ ...p, payment_status: v }))}>
                      <SelectTrigger data-testid="expense-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PAYMENT_STATUS_OPTIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Ödeme Hesabı</Label>
                  <Input value={formData.payment_account} onChange={(e) => setFormData(p => ({ ...p, payment_account: e.target.value }))} placeholder="Ör: Banka hesabı, nakit" data-testid="expense-account-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Açıklama</Label>
                  <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring min-h-[80px]"
                    value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} placeholder="Detaylı açıklama..." data-testid="expense-description-input" />
                </div>
                <div className="space-y-1.5">
                  <Label>Görünürlük Seviyesi</Label>
                  <Select value={formData.visibility} onValueChange={(v) => setFormData(p => ({ ...p, visibility: v }))}>
                    <SelectTrigger data-testid="expense-visibility-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VISIBILITY_OPTIONS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#595959] mt-1">
                    {formData.visibility === 'yonetim' && 'Bu gider sadece yönetim tarafından görülecek'}
                    {formData.visibility === 'ozet' && 'Sakinler sadece ad, tutar ve kategori bilgisini görecek'}
                    {formData.visibility === 'tam_detay' && 'Sakinler tüm detayları ve belgeleri görecek'}
                  </p>
                </div>
                <Button type="submit" className="w-full bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="expense-submit-btn">
                  {editingId ? 'Güncelle' : 'Gider Ekle'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <Card className="border border-[#E5E5E5]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#595959]">Toplam Gider</p>
                  <TrendingUp className="w-4 h-4 text-[#595959]" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-[#1A1A2E] mt-1" data-testid="total-expense">{fmt(summary.total_amount)} TL</p>
                <p className="text-xs text-[#595959]">{summary.total_count} kayıt</p>
              </CardContent>
            </Card>
            <Card className="border border-[#E5E5E5]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#595959]">Ödenen</p>
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-green-700 mt-1" data-testid="paid-expense">{fmt(summary.paid_amount)} TL</p>
              </CardContent>
            </Card>
            <Card className="border border-[#E5E5E5]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#595959]">Bekleyen</p>
                  <Clock className="w-4 h-4 text-amber-600" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-amber-700 mt-1" data-testid="pending-expense">{fmt(summary.pending_amount)} TL</p>
              </CardContent>
            </Card>
            <Card className="border border-[#E5E5E5]">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#595959]">Kategori Sayısı</p>
                  <Wallet className="w-4 h-4 text-[#595959]" />
                </div>
                <p className="text-xl sm:text-2xl font-bold text-[#1A1A2E] mt-1">{summary.by_category?.length || 0}</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-[180px]" data-testid="filter-category"><SelectValue placeholder="Kategori" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Kategoriler</SelectItem>
              {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterVisibility} onValueChange={setFilterVisibility}>
            <SelectTrigger className="w-[180px]" data-testid="filter-visibility"><SelectValue placeholder="Görünürlük" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Seviyeler</SelectItem>
              {VISIBILITY_OPTIONS.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block">
          <Card className="border border-[#E5E5E5]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Masraf</TableHead>
                  <TableHead>Kategori</TableHead>
                  <TableHead className="text-right">Tutar</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Görünürlük</TableHead>
                  <TableHead>Belgeler</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-[#595959]">Henüz gider kaydı yok</TableCell></TableRow>
                ) : expenses.map((exp) => (
                  <TableRow key={exp.id} data-testid={`expense-row-${exp.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-medium text-[#1A1A2E]">{exp.name}</p>
                        {exp.description && <p className="text-xs text-[#595959] truncate max-w-[200px]">{exp.description}</p>}
                      </div>
                    </TableCell>
                    <TableCell><span className="text-sm">{exp.category_label}</span></TableCell>
                    <TableCell className="text-right font-semibold">{fmt(exp.amount)} TL</TableCell>
                    <TableCell className="text-sm">{exp.date}</TableCell>
                    <TableCell>{getStatusBadge(exp.payment_status)}</TableCell>
                    <TableCell>{getVisibilityBadge(exp.visibility)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {exp.documents.length > 0 && (
                          <span className="text-xs text-[#595959]">{exp.documents.length} belge</span>
                        )}
                        <label className="cursor-pointer">
                          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp"
                            onChange={(e) => { if (e.target.files[0]) handleFileUpload(exp.id, e.target.files[0]); e.target.value = ''; }}
                            disabled={uploading} />
                          <Upload className="w-4 h-4 text-[#595959] hover:text-[#2563EB]" />
                        </label>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(exp)} data-testid={`edit-expense-${exp.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(exp.id)} data-testid={`delete-expense-${exp.id}`}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {expenses.length === 0 ? (
            <Card className="border border-[#E5E5E5]">
              <CardContent className="py-8 text-center text-[#595959]">Henüz gider kaydı yok</CardContent>
            </Card>
          ) : expenses.map((exp) => (
            <Card key={exp.id} className="border border-[#E5E5E5]" data-testid={`expense-card-${exp.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-[#1A1A2E] truncate">{exp.name}</p>
                    <p className="text-xs text-[#595959]">{exp.category_label} - {exp.date}</p>
                  </div>
                  <p className="font-bold text-[#1A1A2E] ml-2 whitespace-nowrap">{fmt(exp.amount)} TL</p>
                </div>
                {exp.description && <p className="text-xs text-[#595959] mb-2 line-clamp-2">{exp.description}</p>}
                <div className="flex items-center gap-2 mb-3">
                  {getStatusBadge(exp.payment_status)}
                  {getVisibilityBadge(exp.visibility)}
                  {exp.documents.length > 0 && (
                    <span className="text-xs text-[#595959] flex items-center gap-1"><FileText className="w-3 h-3" />{exp.documents.length}</span>
                  )}
                </div>
                {/* Document list */}
                {exp.documents.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {exp.documents.map((doc) => (
                      <div key={doc} className="flex items-center justify-between bg-[#F9F8F6] px-2 py-1 rounded text-xs">
                        <a href={`${API_URL}/api/expenses/files/${exp.business_id}/${doc}`} target="_blank" rel="noreferrer" className="text-[#2563EB] hover:underline truncate flex-1">{doc}</a>
                        <button onClick={() => handleDeleteDocument(exp.id, doc)} className="text-red-400 hover:text-red-600 ml-2"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="flex-1 gap-1" onClick={() => handleEdit(exp)}>
                    <Pencil className="w-3 h-3" /> Düzenle
                  </Button>
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.webp"
                      onChange={(e) => { if (e.target.files[0]) handleFileUpload(exp.id, e.target.files[0]); e.target.value = ''; }}
                      disabled={uploading} />
                    <Button variant="outline" size="sm" className="gap-1" asChild><span><Upload className="w-3 h-3" /> Belge</span></Button>
                  </label>
                  <Button variant="outline" size="sm" className="text-red-500 border-red-200" onClick={() => handleDelete(exp.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Category Breakdown */}
        {summary && summary.by_category && summary.by_category.length > 0 && (
          <Card className="border border-[#E5E5E5] mt-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg">Kategoriye Göre Dağılım</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {summary.by_category.map((cat) => {
                  const pct = summary.total_amount > 0 ? (cat.total / summary.total_amount) * 100 : 0;
                  return (
                    <div key={cat.category} className="flex items-center gap-3" data-testid={`category-bar-${cat.category}`}>
                      <span className="text-sm w-28 sm:w-36 truncate text-[#595959]">{cat.label}</span>
                      <div className="flex-1 bg-[#F0F0F0] rounded-full h-3 overflow-hidden">
                        <div className="h-full bg-[#2563EB] rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-sm font-medium w-24 text-right">{fmt(cat.total)} TL</span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  );
};
